import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Typography,
  Tooltip,
  ListItemButton,
  ListItemText,
  IconButton,
  Collapse,
  List,
  alpha,
} from '@mui/material';
import {
  Folder,
  Plus,
  FolderPlus,
  ChevronDown,
  ChevronRight,
  CalendarDays,
  GraduationCap,
  Briefcase,
  Building2,
  Users,
  Lightbulb,
  Microscope,
  Activity,
  Plane,
  Film,
  Home,
  Utensils,
  Laptop,
  Trophy,
  ShoppingCart,
  Target,
  UsersRound,
  Scale,
  ClipboardList,
  BarChart3,
  Video,
  Youtube,
  Code,
  DollarSign,
  BarChart,
  Settings,
  PhoneCall,
  Layout,
  FileText,
  Mic2,
  Music2,
  Radio,
  Headphones,
  Speaker,
  Podcast,
} from 'lucide-mui';
import type { RootState } from '../../../reducers';
import { generateFolderTree } from './folderHelper';
import { FolderType } from '../../../types';
import { getFolderUrl } from '../../../lib/routingUtils';
import { RouteParams } from '../types';
import FolderMenu from './actions';
import { pushModal } from '../../globalModals/globalModalsSlice';
import useLimits from '../../plan/hooks/limits';
import { updateSettings } from '../../settings/settingsSlice';
import { setDrawerState } from '../../dashboard/dashboardSlice';
import useTags from '../../../hooks/useTags';

const ICON_MAP = {
  Folder,
  Video,
  Youtube,
  Mic: Mic2,
  Music: Music2,
  Radio,
  Headphones,
  Speaker,
  Podcast,
  Code,
  DollarSign,
  BarChart,
  Settings,
  PhoneCall,
  Layout,
  FileText,
  CalendarDays,
  GraduationCap,
  Briefcase,
  Building2,
  Users,
  Lightbulb,
  Microscope,
  Activity,
  Plane,
  Film,
  Home,
  Utensils,
  Laptop,
  Trophy,
  ShoppingCart,
  Target,
  UsersRound,
  Scale,
  ClipboardList,
  BarChart3,
};

const FolderListItem = ({
  folder,
  open,
  level = 0,
}: {
  folder: FolderType;
  open: boolean;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const { folderId } = useParams<RouteParams>();
  const { selectedTeam } = useSelector((state: RootState) => ({
    selectedTeam: state.team.selectedTeam,
  }));
  const { getTag } = useTags();
  const folderIconsTag = getTag('folder_icons');
  const folderIcons = folderIconsTag ? JSON.parse(folderIconsTag) : {};
  const iconName = folderIcons[folder._id] || 'Folder';
  const IconComponent = ICON_MAP[iconName as keyof typeof ICON_MAP] || Folder;
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { teamId } = selectedTeam || {};

  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = folderId === folder._id;

  const renderLabel = () => {
    const { name } = folder;

    return (
      <Tooltip title={name} placement="right" arrow disableHoverListener={open}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: open ? 0 : 0.5,
            justifyContent: 'space-between',
            position: 'relative',
            height: '45px',
            py: 1,
            width: '100%',
            '&:hover .folder-menu': {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              textDecoration: 'none',
              color: 'inherit',
              flexGrow: 1,
              '& .MuiSvgIcon-root': {
                transition: (theme) =>
                  theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shorter,
                    easing: theme.transitions.easing.easeOut,
                  }),
              },
            }}
          >
            <Box
              component="span"
              sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}
            >
              <IconComponent />
            </Box>
            {open && <Typography variant="body2">{name}</Typography>}
          </Box>
          <Box
            className="folder-menu"
            sx={{
              opacity: showMenu ? 1 : 0,
              transition: 'opacity 0.2s ease-out',
              height: '100%',
              display: isMobile && !showMenu ? 'none' : 'block',
              '& .MuiSvgIcon-root': {
                transform: 'translateZ(0)',
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                transition: (theme) =>
                  theme.transitions.create('transform', {
                    duration: theme.transitions.duration.shorter,
                    easing: theme.transitions.easing.easeOut,
                  }),
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(true);
            }}
          >
            <FolderMenu folder={folder} />
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const handleFolderClick = () => {
    if (hasChildren) {
      setExpanded(!expanded);
    }

    const url = teamId
      ? getFolderUrl({
          folderId: folder._id,
          teamId,
          withBasePath: true,
        })
      : '/';
    window.location.href = url;

    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
  };

  return (
    <>
      <ListItemButton
        onMouseEnter={() => !isMobile && setShowMenu(true)}
        onMouseLeave={() => !isMobile && setShowMenu(false)}
        sx={{
          pl: 2 + level * 3,
          margin: '4px 4px',
          borderRadius: 2,
          height: '45px',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'transparent',
          '&.Mui-selected': {
            bgcolor: 'transparent',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            backgroundColor: isSelected ? 'secondary.main' : 'secondary.light',
            opacity: isSelected ? 1 : 0,
            borderRadius: 'inherit',
          },
          '&:hover': {
            '&::before': {
              opacity: 1,
            },
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },
        }}
        onClick={handleFolderClick}
      >
        {hasChildren && (
          <Box
            component="span"
            sx={{
              display: 'flex',
              mr: 1,
              ml: -2,
            }}
          >
            {expanded ? <ChevronDown /> : <ChevronRight />}
          </Box>
        )}
        {renderLabel()}
      </ListItemButton>
      {hasChildren && (
        <Collapse in={expanded}>
          <List disablePadding>
            {folder.children.map((child) => (
              <FolderListItem
                key={child._id}
                folder={child}
                open={open}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const FolderTreeView = () => {
  const { folders, dashboard } = useSelector((state: RootState) => ({
    folders: state.library.folders,
    dashboard: state.dashboard,
  }));
  const folderTree = useMemo(() => generateFolderTree(folders.data), [folders]);
  const open = dashboard.drawerState === 'open';
  const dispatch = useDispatch();
  const guardLimits = useLimits();
  const [isHovered, setIsHovered] = useState(false);
  const { folderListExpanded } = useSelector(
    (state: RootState) => state.settings.ui
  );
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  const handleCreateFolder = () => {
    guardLimits('folderManagement', () => {
      dispatch(pushModal({ type: 'create-folder-modal' }));
      if (isMobile) {
        dispatch(setDrawerState('closed'));
      }
    });
  };

  const handleToggleFolderList = () => {
    dispatch(
      updateSettings({
        key: 'ui',
        newSettings: { folderListExpanded: !folderListExpanded },
      })
    );
  };

  return (
    <Box>
      <Tooltip
        title="Create Folder"
        placement="right"
        arrow
        disableHoverListener={open}
      >
        {open ? (
          <ListItemButton
            onClick={handleToggleFolderList}
            sx={(theme) => ({
              justifyContent: 'initial',
              px: 2.5,
              height: '45px',
              position: 'relative',
              overflow: 'hidden',
              margin: '3px 4px',
              borderRadius: 2,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                backgroundColor: alpha(theme.palette.action.hover, 0.5),
                transition: theme.transitions.create(
                  ['opacity', 'background-color'],
                  {
                    duration: theme.transitions.duration.shorter,
                    easing: theme.transitions.easing.easeOut,
                  }
                ),
                opacity: 0,
                borderRadius: 'inherit',
              },
              '&:hover': {
                '&::before': {
                  opacity: 1,
                },
                '& .MuiListItemIcon-root .MuiSvgIcon-root': {
                  transform: 'scale(1.1)',
                },
              },
              '& > *': {
                position: 'relative',
                zIndex: 1,
              },
            })}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <ChevronDown
              sx={{
                position: 'absolute',
                left: isHovered ? '12px' : '-28px',
                transition: 'all 0.15s ease-in-out',
                transform: folderListExpanded
                  ? 'rotate(0deg)'
                  : 'rotate(-90deg)',
              }}
            />
            <ListItemText
              primary="Folders"
              sx={{
                '& .MuiTypography-root': {
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'secondary.contrastText',
                  textTransform: 'uppercase',
                  transition: 'padding-left 0.15s ease-in-out',
                  paddingLeft: isHovered ? '28px' : '0px',
                },
              }}
            />
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleCreateFolder();
              }}
              data-testid="create-folder-button"
              sx={{
                marginLeft: 'auto',
              }}
            >
              <Plus />
            </IconButton>
          </ListItemButton>
        ) : (
          <IconButton
            onClick={handleCreateFolder}
            data-testid="create-folder-button"
            sx={{
              padding: '14px 16px',
              borderRadius: 2,
              margin: '4px 4px',
              width: 'calc(100% - 8px)',
              height: '45px',
              justifyContent: 'flex-start',
              '& .MuiSvgIcon-root': {
                transform: 'translateZ(0)',
                willChange: 'transform',
                backfaceVisibility: 'hidden',
              },
              '&:hover .MuiSvgIcon-root': {
                transform: 'translateZ(0) scale(1.1)',
              },
            }}
          >
            <FolderPlus />
          </IconButton>
        )}
      </Tooltip>

      <Collapse in={folderListExpanded}>
        <List disablePadding>
          {folderTree.map((folder) => (
            <FolderListItem key={folder._id} folder={folder} open={open} />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

export default FolderTreeView;
